import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { EditorState, LexicalEditor } from 'lexical';
import { twMerge } from 'tailwind-merge';
import { faLock, faSend } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { faUserCircle } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { exhaustiveGuard } from '@soundxyz/utils';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { LexicalInput } from '@components/Lexical/Input';
import { Avatar } from '@components/basics/Images/Avatar';
import { Spinner } from '@components/basics/Spinner';
import { useAuth } from '@context/auth/base';
import { useMediaQuery } from '@context/mediaQuery';

type Props = {
  placeholder: string;
  isLocked: boolean;
  onClick: () => void;
  type: 'feed_post' | 'feed' | 'feed_replies_preview';
} & (
  | {
      as: 'default';
    }
  | {
      as: 'input';
      disabled: boolean;
      /*
       * ID to be used to focus component externally using querySelector
       */
      id: string | undefined;
      isLoading: boolean;
      onChange: (editorState: EditorState, editor: LexicalEditor, tags: Set<string>) => void;
      onSubmit: (editorState: EditorState) => void;
    }
);

export function PostInputUI(props: Props) {
  const { authUser } = useAuth();
  const { tablet } = useMediaQuery();

  const { onClick, placeholder, isLocked, as, type } = props;

  const variants = useMemo(() => {
    switch (type) {
      case 'feed':
        return {
          avatarSize: tablet ? 48 : 40,
          avatarPlaceholderClassName: 'text-neutral500 text-avatar md:text-avatar-md',
          avatarClassName: 'md:h-12 md:w-12 h-10 w-10',
          containerClassName: 'gap-2 md:gap-4',
        };
      case 'feed_post':
        return {
          avatarSize: 40,
          avatarPlaceholderClassName: 'text-neutral500 text-avatar',
          avatarClassName: 'h-10 w-10',
          containerClassName: 'gap-2',
        };
      case 'feed_replies_preview':
        return {
          avatarSize: 40,
          avatarPlaceholderClassName: 'text-neutral500 text-avatar',
          avatarClassName: 'h-10 w-10 hidden md:block',
          containerClassName: 'gap-2',
        };
      default:
        exhaustiveGuard(type);
    }
  }, [tablet, type]);

  const sendIcon = (
    <div className="flex aspect-square h-full w-min flex-00auto items-center justify-center rounded-full bg-base800 p-2 opacity-30">
      {'isLoading' in props && props.isLoading ? (
        <Spinner size="sm" className="border-white text-white" />
      ) : isLocked ? (
        <FontAwesomeIcon icon={faLock} className="text-base-xs text-white md:text-base-m" />
      ) : (
        <FontAwesomeIcon icon={faSend} className="text-base-xs text-white md:text-base-m" />
      )}
    </div>
  );

  const renderContent = useStableCallback(() => {
    switch (as) {
      case 'default':
        return (
          <button
            className="relative flex flex-1 items-center rounded-md bg-base100 px-3 py-2 text-base-l text-neutral500 ring-inset ring-base300 hover:bg-neutral50 hover:ring-1"
            onClick={onClick}
          >
            <div className="line-clamp-1 flex-1 text-left">{placeholder}</div>
            {sendIcon}
          </button>
        );
      case 'input':
        return (
          <LexicalInput
            initialMentionData={undefined}
            id={props.id}
            autoFocus={false}
            onChange={props.onChange}
            placeholder={placeholder}
            loading={props.isLoading}
            disabled={props.disabled}
            onSubmit={props.onSubmit}
            className="comment-input w-full"
            characterCount={false}
            type="single-line"
          />
        );
      default:
        exhaustiveGuard(as);
    }
  });

  return (
    <div className={twMerge('flex w-full items-center', variants.containerClassName)}>
      <div
        className={twMerge(
          'flex-shrink-0 rounded-full',
          !authUser && 'bg-neutral500',
          variants.avatarClassName,
        )}
      >
        {authUser ? (
          <Avatar
            walletAddress={authUser.publicAddress}
            username={authUser.username}
            webappUri={authUser.webappUri}
            size={variants.avatarSize}
            src={authUser.avatar?.url}
            borderRadius="50%"
          />
        ) : (
          <div className="flex items-center justify-center bg-white">
            <FontAwesomeIcon icon={faUserCircle} className={variants.avatarPlaceholderClassName} />
          </div>
        )}
      </div>

      {renderContent()}
    </div>
  );
}
